import { useReducer } from "react";
import type { QuickLoginStep } from "./QuickLogin";

export type Step =
  | "login"
  | "new-password"
  | "totp-code"
  | "totp-setup"
  | "cancelling"
  | "forgot-request"
  | "forgot-confirm"
  | QuickLoginStep;

type TotpSetup = { secret: string; via: "challenge" | "enrolment" };

interface LoginStepsState {
  step: Step;
  error: string | null;
  info: string | null;
  loginEmail?: string;
  resetUsername: string;
  totpSetup: TotpSetup | null;
  submitting: boolean;
  changingPin: boolean;
}

type LoginStepsAction =
  | { type: "go"; step: Step }
  | { type: "pinChange" }
  | { type: "loginForm"; email?: string }
  | { type: "failed"; error: string | null }
  | { type: "resetRequired" }
  | { type: "resetCodeSent"; username: string }
  | { type: "passwordReset" }
  | { type: "totpRequired"; totpSetup: TotpSetup }
  | { type: "totpLeft" }
  | { type: "submitting" }
  | { type: "settled" };

function reducer(
  state: LoginStepsState,
  action: LoginStepsAction,
): LoginStepsState {
  const next = step(state, action);
  return next.step === "login" || next.step === "accounts"
    ? { ...next, changingPin: false }
    : next;
}

function step(
  state: LoginStepsState,
  action: LoginStepsAction,
): LoginStepsState {
  const go = (step: Step) => ({ ...state, step, error: null, info: null });
  switch (action.type) {
    case "go":
      return go(action.step);
    case "pinChange":
      return { ...go("account-password"), changingPin: true };
    case "loginForm":
      return { ...go("login"), loginEmail: action.email };
    case "failed":
      return { ...state, error: action.error };
    case "resetRequired":
      return {
        ...go("forgot-request"),
        info: "Your password must be reset before you can log in.",
      };
    case "resetCodeSent":
      return { ...go("forgot-confirm"), resetUsername: action.username };
    case "passwordReset":
      return {
        ...state,
        step: "login",
        info: "Your password has been reset. Please log in.",
      };
    case "totpRequired":
      return { ...go("totp-setup"), totpSetup: action.totpSetup };
    case "totpLeft":
      return { ...go("login"), totpSetup: null };
    case "submitting":
      return { ...state, submitting: true };
    case "settled":
      return { ...state, submitting: false };
  }
}

export function useLoginSteps(initialStep: Step) {
  const [state, dispatch] = useReducer(reducer, {
    step: initialStep,
    error: null,
    info: null,
    resetUsername: "",
    totpSetup: null,
    submitting: false,
    changingPin: false,
  });

  const actions = {
    goTo: (step: Step) => dispatch({ type: "go", step }),
    changePin: () => dispatch({ type: "pinChange" }),
    openLoginForm: (email?: string) => dispatch({ type: "loginForm", email }),
    fail: (error: string | null) => dispatch({ type: "failed", error }),
    resetRequired: () => dispatch({ type: "resetRequired" }),
    resetCodeSent: (username: string) =>
      dispatch({ type: "resetCodeSent", username }),
    passwordReset: () => dispatch({ type: "passwordReset" }),
    totpRequired: (totpSetup: TotpSetup) =>
      dispatch({ type: "totpRequired", totpSetup }),
    totpLeft: () => dispatch({ type: "totpLeft" }),
    submitting: () => dispatch({ type: "submitting" }),
    settled: () => dispatch({ type: "settled" }),
  };

  return { ...state, actions };
}
